import React, { useMemo } from 'react';
import { Card, ListGroup, Stack } from 'react-bootstrap';
import { FaCarSide, FaClock, FaFlagCheckered, FaMapMarkerAlt, FaWalking } from 'react-icons/fa';
import { useTours } from 'features/Responder/features/Tours/contexts/TourContext';

const formatMinutes = (mins) => {
  if (!Number.isFinite(mins)) return null;
  if (mins < 1) return '<1 min';
  if (mins < 60) return `${Math.round(mins)} min`;
  const hours = Math.floor(mins / 60);
  const rem = Math.round(mins % 60);
  return rem ? `${hours}h ${rem}m` : `${hours}h`;
};

const StopRow = ({ stop, index, isStart, isDestination }) => {
  const label =
    stop?.label ||
    stop?.name ||
    (isStart
      ? 'Start'
      : isDestination
        ? 'Destination'
        : `Checkpoint ${String.fromCharCode(64 + index)}`);
  const address = stop?.address;
  const lat = stop?.lat ?? stop?.coordinates?.[0];
  const lng = stop?.lng ?? stop?.coordinates?.[1];
  const timeWindow =
    Number.isFinite(stop?.timeWindowMinutes) && stop.timeWindowMinutes > 0
      ? formatMinutes(stop.timeWindowMinutes)
      : null;
  const dwell =
    Number.isFinite(stop?.stopTimeMinutes) && stop.stopTimeMinutes > 0
      ? formatMinutes(stop.stopTimeMinutes)
      : null;

  return (
    <ListGroup.Item className="d-flex flex-column gap-1">
      <div className="d-flex justify-content-between align-items-center">
        <div className="fw-semibold">
          {label}
          {isStart ? ' (Start)' : isDestination ? ' (Destination)' : ''}
        </div>
        <div className="text-muted small">
          {lat != null && lng != null
            ? `${Number(lat).toFixed(4)}, ${Number(lng).toFixed(4)}`
            : '—'}
        </div>
      </div>
      {address ? <div className="small text-muted">{address}</div> : null}
      <div className="d-flex gap-3 text-muted small">
        {timeWindow ? (
          <span title="Allowed arrival window">
            <FaClock className="me-1" />
            {timeWindow}
          </span>
        ) : null}
        {dwell ? (
          <span title="Planned dwell time">
            <FaClock className="me-1" />
            {dwell}
          </span>
        ) : null}
      </div>
    </ListGroup.Item>
  );
};

export default function TourViewer() {
  const { activeTourData, checkpoints } = useTours();

  const { stops, usingCheckpoints } = useMemo(() => {
    if (Array.isArray(checkpoints) && checkpoints.length) {
      return { stops: checkpoints, usingCheckpoints: true };
    }
    if (Array.isArray(activeTourData?.stops) && activeTourData.stops.length) {
      return { stops: activeTourData.stops, usingCheckpoints: false };
    }
    if (Array.isArray(activeTourData?.nodes) && activeTourData.nodes.length) {
      return { stops: activeTourData.nodes, usingCheckpoints: false };
    }
    return { stops: [], usingCheckpoints: false };
  }, [checkpoints, activeTourData]);

  const travelMode = (activeTourData?.travelType || activeTourData?.travelMode || '').toLowerCase();
  const modeLabel = travelMode.startsWith('walk')
    ? 'Walking'
    : travelMode.startsWith('drive')
      ? 'Driving'
      : 'Not set';

  return (
    <Card className="shadow-sm h-100">
      <Card.Body className="d-flex flex-column gap-3">
        <div>
          <div className="text-muted text-uppercase small">Tour Viewer</div>
          <div className="fw-semibold fs-5">
            {activeTourData?.name || 'Select a tour to preview'}
          </div>
          <div className="small text-muted">
            {stops.length
              ? `${stops.length} checkpoint${stops.length === 1 ? '' : 's'}`
              : 'No checkpoints loaded.'}
          </div>
        </div>

        <Stack direction="horizontal" gap={3} className="flex-wrap">
          <div className="d-flex align-items-center gap-2">
            {modeLabel === 'Walking' ? <FaWalking /> : <FaCarSide />}
            <span className="small text-uppercase text-muted">Mode</span>
            <span className="fw-semibold">{modeLabel}</span>
          </div>
          <div className="d-flex align-items-center gap-2">
            <FaFlagCheckered />
            <span className="small text-uppercase text-muted">Route Type</span>
            <span className="fw-semibold">
              {activeTourData?.routeOptimization
                ? activeTourData.routeOptimization.replace(/_/g, ' ')
                : 'Not set'}
            </span>
          </div>
        </Stack>

        <div>
          <div className="fw-semibold mb-2 d-flex align-items-center gap-2">
            <FaMapMarkerAlt className="text-success" /> Checkpoints
          </div>
          <ListGroup>
            {stops.length ? (
              stops.map((stop, idx) => {
                const isStart = !usingCheckpoints && (idx === 0 || stop?.type === 'origin');
                const isDestination =
                  !usingCheckpoints &&
                  (stop?.type === 'destination' ||
                    (!stop?.type && idx === stops.length - 1 && stops.length > 1));
                return (
                  <StopRow
                    key={stop.id || idx}
                    stop={stop}
                    index={idx + 1}
                    isStart={isStart}
                    isDestination={isDestination}
                  />
                );
              })
            ) : (
              <ListGroup.Item className="text-muted small">
                Select a route from the list to view details.
              </ListGroup.Item>
            )}
          </ListGroup>
        </div>
      </Card.Body>
    </Card>
  );
}
